export type PlcSeries = {
  id: string;
  name: string;
  aliases: string[];
  engineeringSoftware: string[];
  models?: string[];
  group:
    | "current"
    | "classic"
    | "safety"
    | "distributed"
    | "compact"
    | "process"
    | "motion";
};

export type PlcManufacturer = {
  id: string;
  name: string;
  aliases: string[];
  series: PlcSeries[];
};

export const plcManufacturers: PlcManufacturer[] = [
  {
    id: "siemens",
    name: "Siemens",
    aliases: ["SIMATIC"],
    series: [
      {
        id: "siemens-s7-200",
        name: "SIMATIC S7-200",
        aliases: ["S7-200"],
        engineeringSoftware: ["STEP 7 Micro/WIN"],
        group: "classic",
      },
      {
        id: "siemens-s7-200-smart",
        name: "SIMATIC S7-200 SMART",
        aliases: ["S7-200 SMART"],
        engineeringSoftware: ["STEP 7 Micro/WIN SMART"],
        group: "compact",
      },
      {
        id: "siemens-s7-300",
        name: "SIMATIC S7-300",
        aliases: ["S7-300"],
        engineeringSoftware: ["STEP 7 Classic", "TIA Portal"],
        group: "classic",
      },
      {
        id: "siemens-s7-400",
        name: "SIMATIC S7-400",
        aliases: ["S7-400", "S7-400H"],
        engineeringSoftware: ["STEP 7 Classic", "TIA Portal"],
        group: "classic",
      },
      {
        id: "siemens-s7-1200",
        name: "SIMATIC S7-1200",
        aliases: ["S7-1200"],
        engineeringSoftware: ["TIA Portal"],
        group: "current",
      },
      {
        id: "siemens-s7-1200-g2",
        name: "SIMATIC S7-1200 G2",
        aliases: ["S7-1200 G2"],
        engineeringSoftware: ["TIA Portal"],
        group: "current",
      },
      {
        id: "siemens-s7-1500",
        name: "SIMATIC S7-1500",
        aliases: ["S7-1500", "S7-1500F", "S7-1500H"],
        engineeringSoftware: ["TIA Portal"],
        group: "current",
      },
      {
        id: "siemens-et200-cpu",
        name: "SIMATIC ET 200 CPU / Distributed Controllers",
        aliases: ["ET 200SP CPU", "ET 200pro CPU"],
        engineeringSoftware: ["TIA Portal"],
        group: "distributed",
      },
    ],
  },

  {
    id: "rockwell",
    name: "Allen-Bradley / Rockwell Automation",
    aliases: ["Allen-Bradley", "Rockwell"],
    series: [
      {
        id: "ab-plc5",
        name: "PLC-5",
        aliases: ["PLC5"],
        engineeringSoftware: ["RSLogix 5"],
        group: "classic",
      },
      {
        id: "ab-slc500",
        name: "SLC 500",
        aliases: ["SLC500"],
        engineeringSoftware: ["RSLogix 500"],
        group: "classic",
      },
      {
        id: "ab-micrologix",
        name: "MicroLogix",
        aliases: ["MicroLogix 1000", "1100", "1200", "1400", "1500"],
        engineeringSoftware: ["RSLogix 500"],
        group: "classic",
      },
      {
        id: "ab-micro800",
        name: "Micro800",
        aliases: ["Micro810", "Micro820", "Micro830", "Micro850", "Micro870"],
        engineeringSoftware: ["Connected Components Workbench"],
        group: "current",
      },
      {
        id: "ab-compactlogix",
        name: "CompactLogix",
        aliases: ["CompactLogix 5370", "CompactLogix 5380", "CompactLogix 5480"],
        engineeringSoftware: ["Studio 5000 Logix Designer"],
        group: "current",
      },
      {
        id: "ab-controllogix",
        name: "ControlLogix",
        aliases: ["ControlLogix 5570", "ControlLogix 5580"],
        engineeringSoftware: ["Studio 5000 Logix Designer"],
        group: "current",
      },
      {
        id: "ab-guardlogix",
        name: "GuardLogix",
        aliases: ["GuardLogix 5570", "GuardLogix 5580", "Compact GuardLogix"],
        engineeringSoftware: ["Studio 5000 Logix Designer"],
        group: "safety",
      },
    ],
  },

  {
    id: "mitsubishi",
    name: "Mitsubishi Electric",
    aliases: ["MELSEC"],
    series: [
      {
        id: "melsec-a",
        name: "MELSEC-A / AnS / QnA",
        aliases: ["A Series", "AnS", "QnA"],
        engineeringSoftware: ["GX Developer"],
        group: "classic",
      },
      {
        id: "melsec-fx",
        name: "MELSEC-F / FX",
        aliases: ["FX1S", "FX1N", "FX2N", "FX3G", "FX3U", "FX3UC"],
        engineeringSoftware: ["GX Developer", "GX Works2"],
        group: "classic",
      },
      {
        id: "melsec-iq-f",
        name: "MELSEC iQ-F",
        aliases: ["FX5U", "FX5UC", "FX5UJ"],
        engineeringSoftware: ["GX Works3"],
        group: "current",
      },
      {
        id: "melsec-l",
        name: "MELSEC-L",
        aliases: ["L Series"],
        engineeringSoftware: ["GX Works2"],
        group: "classic",
      },
      {
        id: "melsec-q",
        name: "MELSEC-Q",
        aliases: ["Q Series"],
        engineeringSoftware: ["GX Works2"],
        group: "classic",
      },
      {
        id: "melsec-iq-r",
        name: "MELSEC iQ-R",
        aliases: ["iQ-R", "R Series"],
        engineeringSoftware: ["GX Works3"],
        group: "current",
      },
      {
        id: "melsec-safety",
        name: "MELSEC Safety",
        aliases: ["QS Safety", "iQ-R Safety"],
        engineeringSoftware: ["GX Works2", "GX Works3"],
        group: "safety",
      },
    ],
  },

  {
    id: "schneider",
    name: "Schneider Electric",
    aliases: ["Modicon", "Telemecanique"],
    series: [
      {
        id: "modicon-twido",
        name: "Twido",
        aliases: ["Twido"],
        engineeringSoftware: ["TwidoSuite", "TwidoSoft"],
        group: "classic",
      },
      {
        id: "modicon-momentum",
        name: "Modicon Momentum",
        aliases: ["Momentum"],
        engineeringSoftware: ["Concept", "Unity Pro"],
        group: "classic",
      },
      {
        id: "modicon-premium",
        name: "Modicon Premium",
        aliases: ["Premium"],
        engineeringSoftware: ["PL7", "Unity Pro"],
        group: "classic",
      },
      {
        id: "modicon-quantum",
        name: "Modicon Quantum",
        aliases: ["Quantum"],
        engineeringSoftware: ["Concept", "Unity Pro"],
        group: "classic",
      },
      {
        id: "modicon-m171-m173",
        name: "Modicon M171 / M172 / M173",
        aliases: ["M171", "M172", "M173"],
        engineeringSoftware: ["EcoStruxure Machine Expert HVAC"],
        group: "current",
      },
      {
        id: "modicon-m221",
        name: "Modicon M221",
        aliases: ["M221"],
        engineeringSoftware: ["EcoStruxure Machine Expert Basic"],
        group: "current",
      },
      {
        id: "modicon-m241",
        name: "Modicon M241",
        aliases: ["M241"],
        engineeringSoftware: ["EcoStruxure Machine Expert"],
        group: "current",
      },
      {
        id: "modicon-m251",
        name: "Modicon M251",
        aliases: ["M251"],
        engineeringSoftware: ["EcoStruxure Machine Expert"],
        group: "current",
      },
      {
        id: "modicon-m262",
        name: "Modicon M262",
        aliases: ["M262"],
        engineeringSoftware: ["EcoStruxure Machine Expert"],
        group: "current",
      },
      {
        id: "modicon-m340",
        name: "Modicon M340",
        aliases: ["M340"],
        engineeringSoftware: ["EcoStruxure Control Expert"],
        group: "process",
      },
      {
        id: "modicon-m580",
        name: "Modicon M580",
        aliases: ["M580", "M580 Safety"],
        engineeringSoftware: ["EcoStruxure Control Expert"],
        group: "process",
      },
    ],
  },

  {
    id: "omron",
    name: "Omron",
    aliases: ["OMRON"],
    series: [
      {
        id: "omron-c-cv",
        name: "C / CV Series",
        aliases: ["C Series", "CV Series", "C200H"],
        engineeringSoftware: ["CX-Programmer"],
        group: "classic",
      },
      {
        id: "omron-cs",
        name: "CS Series",
        aliases: ["CS1"],
        engineeringSoftware: ["CX-Programmer", "CX-One"],
        group: "classic",
      },
      {
        id: "omron-cj",
        name: "CJ Series",
        aliases: ["CJ1", "CJ2"],
        engineeringSoftware: ["CX-Programmer", "CX-One"],
        group: "classic",
      },
      {
        id: "omron-cp",
        name: "CP Series",
        aliases: ["CP1E", "CP1L", "CP1H", "CP2E"],
        engineeringSoftware: ["CX-Programmer", "CX-One"],
        group: "compact",
      },
      {
        id: "omron-nj",
        name: "NJ Series",
        aliases: ["NJ1", "NJ3", "NJ5"],
        engineeringSoftware: ["Sysmac Studio"],
        group: "current",
      },
      {
        id: "omron-nx",
        name: "NX Series",
        aliases: ["NX1P", "NX102", "NX502", "NX701"],
        engineeringSoftware: ["Sysmac Studio"],
        group: "current",
      },
    ],
  },

  {
    id: "abb",
    name: "ABB",
    aliases: ["AC500"],
    series: [
      {
        id: "abb-ac500-eco",
        name: "AC500-eCo",
        aliases: ["AC500 eCo"],
        engineeringSoftware: ["Automation Builder"],
        group: "compact",
      },
      {
        id: "abb-ac500",
        name: "AC500",
        aliases: ["AC500 V2", "AC500 V3"],
        engineeringSoftware: ["Automation Builder"],
        group: "current",
      },
      {
        id: "abb-ac500-xc",
        name: "AC500-XC",
        aliases: ["AC500 XC"],
        engineeringSoftware: ["Automation Builder"],
        group: "current",
      },
      {
        id: "abb-ac500-s",
        name: "AC500-S",
        aliases: ["AC500 Safety"],
        engineeringSoftware: ["Automation Builder"],
        group: "safety",
      },
    ],
  },

  {
    id: "delta",
    name: "Delta Electronics",
    aliases: ["Delta"],
    series: [
      {
        id: "delta-dvp",
        name: "DVP Series",
        aliases: ["DVP-SS2", "DVP-SA2", "DVP-SX2", "DVP-SE", "DVP-ES2", "DVP-EX2"],
        engineeringSoftware: ["ISPSoft", "WPLSoft"],
        group: "compact",
      },
      {
        id: "delta-as",
        name: "AS Series",
        aliases: ["AS200", "AS300"],
        engineeringSoftware: ["ISPSoft"],
        group: "current",
      },
      {
        id: "delta-ah",
        name: "AH Series",
        aliases: ["AH500", "AH Motion"],
        engineeringSoftware: ["ISPSoft"],
        group: "current",
      },
    ],
  },

  {
    id: "beckhoff",
    name: "Beckhoff",
    aliases: ["TwinCAT"],
    series: [
      {
        id: "beckhoff-cx7000",
        name: "CX7000",
        aliases: ["CX7000 Series"],
        engineeringSoftware: ["TwinCAT 3"],
        group: "compact",
      },
      {
        id: "beckhoff-cx8000",
        name: "CX8000",
        aliases: ["CX8000 Series"],
        engineeringSoftware: ["TwinCAT 2", "TwinCAT 3"],
        group: "classic",
      },
      {
        id: "beckhoff-cx8100",
        name: "CX8100",
        aliases: ["CX8100 Series"],
        engineeringSoftware: ["TwinCAT 3"],
        group: "current",
      },
      {
        id: "beckhoff-cx8200",
        name: "CX8200",
        aliases: ["CX8200 Series"],
        engineeringSoftware: ["TwinCAT 3"],
        group: "current",
      },
      {
        id: "beckhoff-cx2000",
        name: "CX2000",
        aliases: ["CX20xx"],
        engineeringSoftware: ["TwinCAT 3"],
        group: "current",
      },
    ],
  },

  {
    id: "wago",
    name: "WAGO",
    aliases: ["WAGO PLC"],
    series: [
      {
        id: "wago-750-controller",
        name: "750 Series Controllers",
        aliases: ["WAGO-I/O-SYSTEM 750 Controller"],
        engineeringSoftware: ["CODESYS", "WAGO-I/O-PRO"],
        group: "classic",
      },
      {
        id: "wago-pfc100",
        name: "PFC100",
        aliases: ["PFC100 G2"],
        engineeringSoftware: ["CODESYS", "e!COCKPIT"],
        group: "current",
      },
      {
        id: "wago-pfc200",
        name: "PFC200",
        aliases: ["PFC200", "PFC200 XTR"],
        engineeringSoftware: ["CODESYS", "e!COCKPIT"],
        group: "current",
      },
    ],
  },

  {
    id: "phoenix-contact",
    name: "Phoenix Contact",
    aliases: ["PLCnext"],
    series: [
      {
        id: "plcnext-axc-f-1152",
        name: "PLCnext AXC F 1152",
        aliases: ["AXC F 1152"],
        engineeringSoftware: ["PLCnext Engineer"],
        group: "current",
      },
      {
        id: "plcnext-axc-f-1252",
        name: "PLCnext AXC F 1252",
        aliases: ["AXC F 1252"],
        engineeringSoftware: ["PLCnext Engineer"],
        group: "current",
      },
      {
        id: "plcnext-axc-f-2152",
        name: "PLCnext AXC F 2152",
        aliases: ["AXC F 2152"],
        engineeringSoftware: ["PLCnext Engineer"],
        group: "current",
      },
      {
        id: "plcnext-axc-f-3152",
        name: "PLCnext AXC F 3152",
        aliases: ["AXC F 3152"],
        engineeringSoftware: ["PLCnext Engineer"],
        group: "current",
      },
      {
        id: "plcnext-rfc-4072",
        name: "PLCnext RFC 4072",
        aliases: ["RFC 4072S", "RFC 4072R"],
        engineeringSoftware: ["PLCnext Engineer"],
        group: "current",
      },
    ],
  },

  {
    id: "unitronics",
    name: "Unitronics",
    aliases: ["Unitronics PLC"],
    series: [
      {
        id: "unitronics-jazz-m91",
        name: "Jazz / M91",
        aliases: ["Jazz", "M91"],
        engineeringSoftware: ["U90 Ladder"],
        group: "compact",
      },
      {
        id: "unitronics-samba",
        name: "Samba",
        aliases: ["Samba PLC+HMI"],
        engineeringSoftware: ["VisiLogic"],
        group: "compact",
      },
      {
        id: "unitronics-vision",
        name: "Vision",
        aliases: ["Vision PLC+HMI"],
        engineeringSoftware: ["VisiLogic"],
        group: "current",
      },
      {
        id: "unitronics-unistream",
        name: "UniStream",
        aliases: ["UniStream PLC"],
        engineeringSoftware: ["UniLogic"],
        group: "current",
      },
    ],
  },

  {
    id: "keyence",
    name: "KEYENCE",
    aliases: ["Keyence"],
    series: [
      {
        id: "keyence-kv-nano",
        name: "KV Nano",
        aliases: ["KV Nano Series"],
        engineeringSoftware: ["KV STUDIO"],
        group: "compact",
      },
      {
        id: "keyence-kv-8000",
        name: "KV-8000",
        aliases: ["KV-8000 Series"],
        engineeringSoftware: ["KV STUDIO"],
        group: "current",
      },
      {
        id: "keyence-kv-x",
        name: "KV-X",
        aliases: ["KV-X Series"],
        engineeringSoftware: ["KV STUDIO"],
        group: "current",
      },
    ],
  },

  {
    id: "panasonic",
    name: "Panasonic Industry",
    aliases: ["Panasonic PLC", "FP Series"],
    series: [
      {
        id: "panasonic-fp0r",
        name: "FP0R",
        aliases: ["FP0R"],
        engineeringSoftware: ["Control FPWIN Pro7", "Control FPWIN GR7"],
        group: "classic",
      },
      {
        id: "panasonic-fp0h",
        name: "FP0H",
        aliases: ["FP0H"],
        engineeringSoftware: ["Control FPWIN Pro7", "Control FPWIN GR7"],
        group: "current",
      },
      {
        id: "panasonic-fp-xh",
        name: "FP-XH",
        aliases: ["FP-XH"],
        engineeringSoftware: ["Control FPWIN Pro7", "Control FPWIN GR7"],
        group: "current",
      },
      {
        id: "panasonic-fp-x0",
        name: "FP-X0",
        aliases: ["FP-X0"],
        engineeringSoftware: ["Control FPWIN Pro7"],
        group: "compact",
      },
      {
        id: "panasonic-fp7",
        name: "FP7",
        aliases: ["FP7"],
        engineeringSoftware: ["Control FPWIN Pro7"],
        group: "current",
      },
    ],
  },

  {
    id: "ls-electric",
    name: "LS ELECTRIC",
    aliases: ["LS PLC", "LSIS"],
    series: [
      {
        id: "ls-xgb",
        name: "XGB",
        aliases: ["XBC", "XEC", "XBM"],
        engineeringSoftware: ["XG5000"],
        group: "compact",
      },
      {
        id: "ls-xgk",
        name: "XGK",
        aliases: ["XGK Series"],
        engineeringSoftware: ["XG5000"],
        group: "current",
      },
      {
        id: "ls-xgi",
        name: "XGI",
        aliases: ["XGI Series"],
        engineeringSoftware: ["XG5000"],
        group: "current",
      },
      {
        id: "ls-xgr",
        name: "XGR",
        aliases: ["XGR Redundancy PLC"],
        engineeringSoftware: ["XG5000"],
        group: "process",
      },
    ],
  },

  {
    id: "automationdirect",
    name: "AutomationDirect",
    aliases: ["Koyo", "Do-more"],
    series: [
      {
        id: "ad-click",
        name: "CLICK / CLICK PLUS",
        aliases: ["CLICK PLC", "CLICK PLUS"],
        engineeringSoftware: ["CLICK Programming Software"],
        group: "compact",
      },
      {
        id: "ad-brx",
        name: "Do-more BRX",
        aliases: ["BRX", "Do-more"],
        engineeringSoftware: ["Do-more Designer"],
        group: "current",
      },
    ],
  },

  {
    id: "br-automation",
    name: "B&R Industrial Automation",
    aliases: ["B&R"],
    series: [
      {
        id: "br-x20-compact-s",
        name: "X20 Compact-S PLC",
        aliases: ["X20 Compact-S"],
        engineeringSoftware: ["Automation Studio"],
        group: "compact",
      },
      {
        id: "br-x20-embedded",
        name: "X20 Embedded PLC",
        aliases: ["X20 Embedded"],
        engineeringSoftware: ["Automation Studio"],
        group: "current",
      },
      {
        id: "br-x20-plc",
        name: "X20 PLC",
        aliases: ["X20CP"],
        engineeringSoftware: ["Automation Studio"],
        group: "current",
      },
    ],
  },

  {
    id: "bosch-rexroth",
    name: "Bosch Rexroth",
    aliases: ["Rexroth", "ctrlX"],
    series: [
      {
        id: "bosch-ctrlx-core",
        name: "ctrlX CORE",
        aliases: ["ctrlX CORE", "ctrlX COREplus"],
        engineeringSoftware: ["ctrlX PLC Engineering", "ctrlX WORKS"],
        group: "current",
      },
    ],
  },

  {
    id: "honeywell",
    name: "Honeywell",
    aliases: ["ControlEdge"],
    series: [
      {
        id: "honeywell-controledge-plc",
        name: "ControlEdge PLC",
        aliases: ["ControlEdge PLC"],
        engineeringSoftware: ["ControlEdge Builder / Engineering tools"],
        group: "process",
      },
      {
        id: "honeywell-controledge-discrete",
        name: "ControlEdge Discrete PLC",
        aliases: ["ControlEdge Discrete PLC"],
        engineeringSoftware: ["ControlEdge engineering environment"],
        group: "current",
      },
      {
        id: "honeywell-controledge-pcd",
        name: "ControlEdge PCD",
        aliases: ["ControlEdge PCD"],
        engineeringSoftware: ["ControlEdge engineering environment"],
        group: "process",
      },
    ],
  },

  {
    id: "emerson",
    name: "Emerson",
    aliases: ["PACSystems", "GE Intelligent Platforms"],
    series: [
      {
        id: "emerson-rx3i",
        name: "PACSystems RX3i / RX3i Edge Controller",
        aliases: ["RX3i", "RX3i Edge"],
        engineeringSoftware: ["PAC Machine Edition"],
        group: "process",
      },
    ],
  },
];

export function getPlcManufacturer(id: string) {
  return plcManufacturers.find(
    (manufacturer) => manufacturer.id === id,
  );
}

export function getPlcSeries(
  manufacturerId: string,
  seriesId: string,
) {
  return getPlcManufacturer(manufacturerId)?.series.find(
    (series) => series.id === seriesId,
  );
}

export function searchPlcCatalog(query: string) {
  const q = query.trim().toLowerCase();

  if (!q) return plcManufacturers;

  return plcManufacturers
    .map((manufacturer) => {
      const manufacturerMatch = [
        manufacturer.name,
        ...manufacturer.aliases,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);

      const matchingSeries = manufacturer.series.filter(
        (series) =>
          [
            series.name,
            ...series.aliases,
            ...series.engineeringSoftware,
          ]
            .join(" ")
            .toLowerCase()
            .includes(q),
      );

      if (manufacturerMatch) return manufacturer;

      if (matchingSeries.length > 0) {
        return {
          ...manufacturer,
          series: matchingSeries,
        };
      }

      return null;
    })
    .filter(
      (manufacturer): manufacturer is PlcManufacturer =>
        manufacturer !== null,
    );
}
